import { useTranslation } from 'react-i18next';

import { Button, ChatOutline, CogOutline, HStack, SkeletonCircle, SkeletonText, Text, ViewGridAddOutline, VStack } from '@bonusly/design-system';

import type { BizyPeopleBrowserEntry } from '../../../lib/bizy/types';
import { useBizyConnectorCounts } from '../hooks/use-bizy-connector-counts';
import { useBizyPeopleEntries } from '../hooks/use-bizy-people-entries';
import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';
import { BizyPeopleEmptyState } from './bizy-people-empty-state';
import { BizyPeopleList } from './bizy-people-list';

// Shared People Browser content (BIZY-636): "Chat with Bizy", the "Bizy noticed"
// list, an "All memories…" link, and a Connectors / Bizy settings footer. Used
// by both the fullscreen left sidebar (BizyPeopleBrowser) and the compact
// navigation menu (BizySidePanelMenu) so the two surfaces stay identical.

export function BizyPeopleBrowserBody() {
	const { t } = useTranslation('admin_bizy');
	const showSettings = useBizySidePanelStore((state) => state.showSettings);
	const closeHistoryDrawer = useBizySidePanelStore((state) => state.closeHistoryDrawer);
	const clearViewedPerson = useBizySidePanelStore((state) => state.clearViewedPerson);
	const viewPerson = useBizySidePanelStore((state) => state.viewPerson);
	const viewedPerson = useBizySidePanelStore((state) => state.viewedPerson);
	const focusedPersonId = useBizySidePanelStore((state) => state.focusedPersonId);

	const { entries, isLoading } = useBizyPeopleEntries(true);
	const { connected: connectorsConnected, total: connectorsTotal } = useBizyConnectorCounts();

	const goToChat = () => {
		clearViewedPerson();
		closeHistoryDrawer();
	};

	const handleSelectPerson = (entry: BizyPeopleBrowserEntry) => {
		// Open the person's detail view; the person view marks the moment seen on
		// view. Compact: dismiss the menu overlay so the person page is visible.
		viewPerson({ id: entry.person.id, name: entry.person.name ?? '', observedMomentId: entry.observedMomentId });
		closeHistoryDrawer();
	};

	// Icons are passed as plain children (not leftIcon/rightIcon) so the button's
	// icon slot doesn't constrain their size.
	const navButtonProps = {
		justifyContent: 'flex-start' as const,
		variant: 'ghost' as const,
		colorScheme: 'secondary' as const,
		size: 'sm' as const,
		gap: 2,
		px: 2.5,
		py: 2,
		h: 'auto',
		borderRadius: 'md',
		w: '100%',
	};

	return (
		<VStack align="stretch" spacing={0} h="100%" minH={0} flex={1}>
			<VStack align="stretch" spacing={0} flex={1} minH={0} overflowY="auto" px={3} py={3}>
				<Button onClick={goToChat} {...navButtonProps}>
					<ChatOutline size="md" />
					<Text as="span" size="sm" variant="medium">
						{t('menu.chatWithBizy')}
					</Text>
				</Button>

				<Text px={2.5} pt={3} pb={1} size="sm" color="text.softest">
					{t('menu.bizyNoticed')}
				</Text>
				{isLoading && entries.length === 0 ? (
					<VStack align="stretch" spacing={0}>
						{[0, 1, 2].map((row) => (
							<HStack key={row} spacing={2} px={2.5} py={2}>
								<SkeletonCircle boxSize="24px" flexShrink={0} />
								<SkeletonText flex={1} noOfLines={2} spacing={1.5} skeletonHeight="10px" />
							</HStack>
						))}
					</VStack>
				) : entries.length === 0 ? (
					<BizyPeopleEmptyState px={2.5} mt={2.5} />
				) : (
					<BizyPeopleList
						entries={entries}
						onSelectPerson={handleSelectPerson}
						selectedObservedMomentId={viewedPerson?.observedMomentId}
						focusedPersonId={focusedPersonId}
					/>
				)}

				{/* TODO(BIZY follow-up): wire up "All memories…" once its destination is defined.
				<Box px={2.5} pt={3}>
					<Button variant="link" size="sm" color="text.interactive" _hover={{ textDecoration: 'underline' }}>
						{t('menu.allMemories')}
					</Button>
				</Box>
				*/}
			</VStack>

			<VStack align="stretch" spacing={0} px={3} py={2} borderTopWidth="1px" borderColor="border.soft" flexShrink={0}>
				<Button onClick={() => showSettings('connections')} {...navButtonProps}>
					<ViewGridAddOutline size="md" />
					<Text as="span" size="sm" variant="medium" flex={1} textAlign="left">
						{t('menu.connectors')}
					</Text>
					<Text as="span" fontSize="sm" color="text.muted">
						{`${connectorsConnected}/${connectorsTotal}`}
					</Text>
				</Button>
				<Button onClick={() => showSettings()} {...navButtonProps}>
					<CogOutline size="md" />
					<Text as="span" size="sm" variant="medium">
						{t('menu.bizySettings')}
					</Text>
				</Button>
			</VStack>
		</VStack>
	);
}
